import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    forwardRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    ViewChild,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
    Validators,
} from '@angular/forms';
import { IOption } from '@cmps/ng-select';
import {
    CmpsConfirmDialogComponent,
    CmpsConfirmDialogOptions,
    CmpsDialog,
} from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { timer } from 'rxjs';
import { filter, tap } from 'rxjs/operators';

import { AuthService } from '../auth/auth.service';
import { NoteEntity } from '../coaching-base/config';
import {
    CmpsForm,
    CmpsFormBuilder,
    CmpsFormConnector,
    CmpsFormService,
} from '../coaching-observation/experimental.form';
import {
    READONLY_OBSERVATION,
    ReadonlyObservation,
} from '../coaching-observation/observation/observation.model';
import {
    takeUntilDestroy,
    TakeUntilDestroy,
} from '../common/take-until-destroy.decorator';

import { NoteDetailFormValue } from './note-detail-form-value.model';
import {
    NoteDetailFormFields,
    noteDetailFormValueTransformer,
} from './note-detail.form';
import { NoteMenuItem, NoteMenuItemType } from './note-menu-item';
import { TranslationKeyMap } from './notes.i18n';

@Component({
    selector: '[cmpsNoteDetailNext]',
    templateUrl: './note-detail.component.html',
    host: {
        '[attr.data-diffitemid]': '_value.diffItemId',
        'data-e2e': 'component-note',
        '[tabindex]': '-1',
        '(focus)': 'focus()',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NoteDetailComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => NoteDetailComponent),
            multi: true,
        },
    ],
})
@TakeUntilDestroy()
export class NoteDetailComponent
    implements
        OnInit,
        OnDestroy,
        ControlValueAccessor,
        Validator,
        CmpsFormConnector
{
    @ViewChild(EditorComponent) /** @private */
    readonly _editor: EditorComponent;

    @Input() config: NoteEntity;
    @Input() placeholder: string;
    @Input() hasTagBar: boolean;
    @Input() hasCategoryBar: boolean;
    @Input() showHeader: boolean;
    @Input() index: number;
    @Input() required: boolean;
    @Input() deletable: boolean;
    @Input() cmpsFormControlPath: string[];
    @Input() formControl: FormControl;
    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _NoteDetailFormFields = NoteDetailFormFields;
    /** @private */ _noteCategoryOpts: IOption[];
    /** @private */ _form: CmpsForm<NoteDetailFormFields>;
    private _blackout: boolean;
    private _item: NoteDetailFormValue;
    private _onChange: (_: any) => void;
    private _onTouched: (_: any) => void;
    private _onValidatorChange: (_: any) => void;
    private _onRemove: () => void;
    /** @private */ _didFirstLoad: boolean;
    /** @private */ _show: boolean;
    /** @private */ _isCollapsed: boolean = true;
    /** @private */ _categoryName: string;

    // TODO types
    /** @private */ _init = {
        menubar: false,
        toolbar:
            'formatselect bold italic link bullist numlist blockquote undo redo',
        statusbar: false,
        default_link_target: '_blank',
        target_list: false,
        placeholder: null,
        autoresize_bottom_margin: 0,
        browser_spellcheck: true,
        contextmenu: false,
        // content_css: 'default',
        content_style:
            'p { margin-block-start: 0.1rem; margin-block-end: 0.1rem; } ' +
            'body { background-color: transparent; line-height: 1; }',
        setup: () => {
            this._changeDetectorRef.detectChanges();
            // TODO revise for versions > 3.6.1. needed 'cause editor doesn't disable when setDisabledState is called before underlying editor is ready
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._editor &&
                this._editor.setDisabledState(this._form.form.disabled);
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._el.nativeElement.setAttribute(
                'data-ready',
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this._editor && this._editor.editor ? 'true' : 'false',
            );
        },
        plugins: 'autoresize link lists',
    };

    /** @private */ _showToolbar: boolean;

    /** @private */ _menuOpts: NoteMenuItem[];

    /** @private */ get _value(): NoteDetailFormValue {
        return this._form.form.value;
    }

    constructor(
        private readonly _el: ElementRef,
        private readonly _changeDetectorRef: ChangeDetectorRef,
        private readonly _translateService: TranslateService,
        private readonly _formBuilder: CmpsFormBuilder,
        @Optional() private readonly _formService: CmpsFormService,
        private readonly _dialog: CmpsDialog,
        @Optional()
        @Inject(READONLY_OBSERVATION) /** @private */
        readonly _readonly: ReadonlyObservation,
        private readonly _authService: AuthService,
    ) {
        this.deletable = true;
    }

    ngOnInit(): void {
        this._init.placeholder = this.placeholder;

        if (this.hasCategoryBar) {
            this._noteCategoryOpts = this.config.noteCategories.map((e) => ({
                value: e.noteCategoryId,
                label: e.noteCategory,
            }));
        }

        this._form =
            this._formBuilder.create<NoteDetailFormFields>(
                NoteDetailFormFields,
            );
        const formControlName = String(
            (this.formControl?.parent as FormArray)?.controls?.indexOf(
                this.formControl,
            ),
        );
        if (this.cmpsFormControlPath != null && formControlName != null) {
            this._formService?.register(
                this.cmpsFormControlPath.concat(formControlName),
                this._form.form,
            );
        }

        // TODO revise for versions > 3.6.1. https://github.com/tinymce/tinymce-angular/blob/v3.6.1/tinymce-angular-component/src/main/ts/editor/editor.component.ts#L176
        let skip = false;

        this._form.form.valueChanges
            .pipe(
                filter(() => !this._blackout),
                filter(() => {
                    if (!skip && this._value && this._value.contents != null) {
                        skip = true;
                        return false;
                    }
                    return true;
                }),
                tap((value) => {
                    console.log('_onChange note-detail', value);
                    const next: NoteDetailFormValue = {
                        ...value,
                        trackingTime: Date.now(),
                        changed: true,
                    };
                    // TODO revise. better change check operability
                    this._form.form.patchValue(
                        {
                            trackingTime: next.trackingTime,
                            changed: next.changed,
                            updatedByFirstName:
                                this._authService.user.firstName,
                            updatedByLastName: this._authService.user.lastName,
                        },
                        { emitEvent: false },
                    );

                    this._determineCategoryName();

                    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                    this._onChange && this._onChange(next);
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._loadContextMenu();
    }

    ngOnDestroy(): void {
        const formControlName = String(
            (this.formControl?.parent as FormArray)?.controls?.indexOf(
                this.formControl,
            ),
        );
        if (this.cmpsFormControlPath != null && formControlName != null) {
            this._formService?.unregister(
                this.cmpsFormControlPath.concat(formControlName),
            );
        }
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._form && this._form.destroy();
    }

    registerOnChange(fn: any): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        isDisabled
            ? this._form.form.disable({ emitEvent: false })
            : this._form.form.enable({ emitEvent: false });

        this._loadContextMenu();
    }

    writeValue(obj: any): void {
        this._item = obj || {};
        this._patchForm();
        if (this._item.inEditMode === true) {
            this._isCollapsed = false;
        }
        this._delayInitialEditorRender();
    }

    registerOnValidatorChange(fn: () => void): void {
        this._onValidatorChange = fn;
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this._form.form.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
        });
        this._form.checkWarningsErrors();
        let errors = this._form.form.errors;
        const ret = this._form.rollupErrors();
        if (
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            ret &&
            ret._controls &&
            ret._controls[NoteDetailFormFields.CONTENTS.name] &&
            // eslint-disable-next-line @typescript-eslint/dot-notation
            ret._controls[NoteDetailFormFields.CONTENTS.name]['required']
        ) {
            errors = errors || {};
            errors.required = true;
        }
        return errors;
    }

    focus(): void {
        setTimeout(() => {
            this._isCollapsed = false;
            this._showToolbar = true; // TODO revise for versions > 3.6.1. with tinymce it seems like onFocus doesn't always emit when focus is called
            this._editor.editor.focus();
        }, 300);
    }

    /** ensure toolbar dismiss when editor.focus is called elsewhere */
    blur(): void {
        setTimeout(() => {
            this._showToolbar = false;
            this._isCollapsed = true;
        }, 300);
    }

    /** @private */ _onCancelClick(): void {
        if (this._value.changed) {
            // TODO revise form.pristine?
            this._discard();
        } else {
            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            this._onRemove && this._onRemove();
        }
    }

    private _patchForm(): void {
        this._blackout = true;

        if (!this._didFirstLoad) {
            this._didFirstLoad = true;
        }

        this._form.clearWarningsErrors();

        if (this.required) {
            this._form.setValidators(NoteDetailFormFields.CONTENTS, [
                Validators.required,
            ]);
        }

        this._form.form.reset(this._item, { emitEvent: false });

        this._determineCategoryName();
        this._loadContextMenu();

        this._blackout = false;
    }

    onRemove(fn: any): void {
        this._onRemove = fn;
    }

    private _discard(): void {
        const data: CmpsConfirmDialogOptions = {
            title: this._translateService.instant(
                TranslationKeyMap._9492_WARNING,
            ),
            message: this._translateService.instant(
                TranslationKeyMap._9699_DISCARD_CHANGES_,
            ),
            actions: [
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9297_YES,
                    ),
                    data: 0,
                },
                {
                    label: this._translateService.instant(
                        TranslationKeyMap._9298_NO,
                    ),
                    data: 1,
                },
            ],
        };
        const ref = this._dialog.open(CmpsConfirmDialogComponent, { data });
        ref.result
            .pipe(
                tap((ret) => {
                    if (ret !== 0) {
                        return;
                    }

                    if (this._value.noteId != null) {
                        const orig = this._value.trackingOriginal;
                        const next = noteDetailFormValueTransformer(
                            this._value.trackingOriginal,
                        );
                        next.trackingId = this._value.trackingId;
                        next.trackingTime = this._value.trackingTime;
                        next.trackingOriginal = orig;

                        this._item = next;
                        this._patchForm();

                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        this._onChange && this._onChange(next);
                    } else {
                        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                        this._onRemove && this._onRemove();
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /** @private */ _onMenuItemClick(item: NoteMenuItem): void {
        if (item.type === NoteMenuItemType.DELETE) {
            this._onCancelClick();
        }
    }

    private _determineCategoryName(): void {
        this._categoryName = this.config?.noteCategories?.find(
            (e) => e.noteCategoryId === this._value.category,
        )?.noteCategory;
    }

    private _loadContextMenu(): void {
        this._menuOpts = [];

        if (!this._form.form.disabled && this.deletable) {
            this._menuOpts.push({
                type: NoteMenuItemType.DELETE,
                label: this._translateService.instant(
                    TranslationKeyMap._7424_DELETE,
                ),
                icon: 'delete',
            });
        }
    }

    /** attempt to slightly delay the underlying editor iframe,
     * so that the browser doesn't wait for it when doing/scheduling a significant painting
     * in the context of a full commitment/observation form load. maybe iframe lazy-load */
    private _delayInitialEditorRender(): void {
        if (this._show) {
            return;
        }
        timer(50)
            .pipe(
                tap(() => {
                    this._show = true;
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }
}
